import { useCreateCheckIn } from './useCheckIns';
import { useAssignLockerKey } from './useLockers';
import { useToastContext } from './useToastContext';
import { isCheckInCooldownError, CHECK_IN_COOLDOWN_MESSAGE, logger } from '@/lib';
import type { Member } from '@/types';

interface SubmitArgs {
  member: Member;
  /** Free locker to hand out with this check-in; assigned only after the check-in succeeds. */
  lockerId?: string;
  /**
   * Fires once the CHECK-IN itself succeeded (regardless of the locker outcome) —
   * call sites close their modal and run per-site invalidations here.
   */
  onSuccess?: () => void;
}

/**
 * The one check-in submit sequence shared by every CheckInModal host
 * (CheckInsPage, MemberViewPage, Layout header): check in first, then — only if
 * that succeeded — optionally hand out a locker key. Activity logging and query
 * invalidation live inside the wrapped mutations; this hook only sequences and
 * toasts. A failed key handout after a successful check-in is surfaced as a
 * partial success (the member IS in the gym) — never rolled back.
 */
export function useCheckInWithLocker() {
  const createCheckIn = useCreateCheckIn();
  const assignKey = useAssignLockerKey();
  const { showToast } = useToastContext();

  // Every await is inside try/catch so no rejection ever escapes to
  // window.onunhandledrejection (Sentry HOK-OS-2W: raw HK429 cooldown object
  // captured as an unhandled rejection).
  const submit = async ({ member, lockerId, onSuccess }: SubmitArgs): Promise<void> => {
    const memberName = `${member.first_name} ${member.last_name}`;

    try {
      await createCheckIn.mutateAsync({
        memberId: member.id,
        memberName,
        memberEmail: member.email || '',
      });
    } catch (error) {
      showToast(
        isCheckInCooldownError(error) ? CHECK_IN_COOLDOWN_MESSAGE : 'Failed to check in member',
        'error'
      );
      return; // Locker is never attempted; the modal stays open.
    }

    // Guarded like every await above: a host's onSuccess (modal close, query
    // invalidation) throwing would otherwise escape submit() as an unhandled
    // rejection — the exact failure mode this hook exists to prevent.
    try {
      onSuccess?.();
    } catch (err) {
      logger.error('useCheckInWithLocker onSuccess callback failed', err);
    }

    if (!lockerId) {
      showToast(`${memberName} checked in successfully`, 'success');
      return;
    }

    try {
      await assignKey.mutateAsync({ locker_id: lockerId, member_id: member.id });
      showToast(`${memberName} checked in — locker key given out`, 'success');
    } catch (error) {
      // Partial success: the check-in stands, only the key handout failed
      // (e.g. 23505 race — useAssignLockerKey already maps it to a readable message).
      showToast(
        `${memberName} checked in, but the locker key could not be given out: ` +
          `${error instanceof Error ? error.message : 'Unknown error.'} ` +
          'Assign a key from the Lockers page.',
        'error'
      );
    }
  };

  return {
    submit,
    isPending: createCheckIn.isPending || assignKey.isPending,
  };
}
